import { useMemo } from "react";
import { generateAvailabilityTimeSlots } from "@/features/availability/utils/generateAvailabilityTimeSlots";
import type { TimeSlot } from "@/features/availability/types/availabilityTypes";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useServices } from "@/features/services/hooks/serviceQueries";
import type { Service } from "@/features/services/types/serviceTypes";
import { useAppointments } from "./appointmentQueries";
import type { Appointment } from "../types/appointmentTypes";
import { bookingUnavailableReason, hasBookingConflict } from "../utils/bookingTime";
import { useBookingWindows } from "./useBookingWindows";

export function useBookingAvailability(params: {
  dateKey: string;
  durationMinutes: number;
  before: number;
  after: number;
  locationId: string;
  excludeId?: string;
}) {
  const { business } = useCurrentBusiness();
  const zone = business?.timezone ?? "UTC";
  const availability = useBookingWindows(params);
  const { bounds } = availability;
  // Only nearby bookings can conflict with slots on this day; pad for buffers and long visits.
  const conflictRange = useMemo(() => ({
    startAt: new Date(bounds.startAt.getTime() - 2 * 86_400_000),
    endAt: new Date(bounds.endAt.getTime() + 2 * 86_400_000),
  }), [bounds]);
  const appointments = useAppointments(conflictRange);
  const services = useServices();
  const calculateAvailability = (windows: TimeSlot[], bookings: Appointment[], catalogue: Service[]) => {
    const slots = generateAvailabilityTimeSlots(windows, bounds.startAt, bounds.endAt, params.durationMinutes, params.before, params.after)
      .filter((slot) => slot.startAt.getTime() > Date.now() &&
        !hasBookingConflict(slot.startAt, slot.endAt, params.before, params.after, bookings, catalogue, params.locationId, params.excludeId));
    return {
      slots,
      getUnavailableReason(startAt: Date, endAt: Date) {
        if (slots.some((slot) => slot.startAt.getTime() === startAt.getTime() && slot.endAt.getTime() === endAt.getTime())) return null;
        return bookingUnavailableReason(startAt, endAt, params.before, params.after, windows, bounds, zone);
      },
    };
  };
  const error = availability.error ?? appointments.error ?? services.error;
  return {
    ...calculateAvailability(error ? [] : availability.data ?? [], appointments.data ?? [], services.data ?? []),
    error,
    isLoading: availability.isLoading || appointments.isLoading || services.isLoading,
    isFetching: availability.isFetching || appointments.isFetching || services.isFetching,
    enabled: availability.enabled,
    async refresh() {
      if (!availability.enabled) throw new Error("Choose a business and location before booking.");
      const [windows, bookings, catalogue] = await Promise.all([
        availability.refetch(), appointments.refetch(), services.refetch(),
      ]);
      if (windows.error || bookings.error || catalogue.error) throw windows.error ?? bookings.error ?? catalogue.error;
      if (!windows.data || !bookings.data || !catalogue.data) throw new Error("Availability could not be verified. Try again.");
      return {
        ...calculateAvailability(windows.data, bookings.data, catalogue.data),
        appointments: bookings.data,
        services: catalogue.data,
      };
    },
  };
}
